import React from 'react';
import PropTypes from 'prop-types';

import { H4 } from 'components/core/Headline';
import { FacilityImage } from 'components/facility/FacilityImage';
import { FacilityImageNotAvailable } from 'components/facility/ImageNotAvailable';
import { Link } from 'components/core/Link';
import { Paragraph, Text } from 'components/core/Typography';
import { ClickToShowNumberButton } from 'components/contact/ClickToShowNumberButton';

import FacilityReviewSummary from './FacilityReviewSummary';
import './HiddenFacilityCardDetails.scss';

export default function HiddenFacilityCardDetails({
	average,
	distance,
	facilityUrl,
	facilityImage,
	hasReviews,
	name,
	reviewAverage,
	streetAddress,
	city,
	state,
	zip,
	noFollowFacilityLink,
	fetchSearchCityPhoneNumber,
	lazyLoad
}) {
	return (
		<div className="hidden-facility-details-container">
			<Link
				className="hidden-facility-image-link"
				href={facilityUrl}
				rel={noFollowFacilityLink ? 'nofollow' : ''}
				hideBorder
				productClick
			>
				{facilityImage ? (
					<FacilityImage
						src={facilityImage}
						alt={`${name} - ${streetAddress}`}
						className="facility-image"
						size="medium"
						lazyLoad={lazyLoad}
					/>
				) : (
					<FacilityImageNotAvailable
						size="medium"
						className="no-facility-image"
					/>
				)}
			</Link>
			<div className="facility-details-links">
				<div className="facility-details">
					<div className="titles">
						<H4 className="facility-title">
							<Link
								href={facilityUrl}
								rel={noFollowFacilityLink ? 'nofollow' : ''}
								hideBorder
								productClick
							>
								<span itemProp="name">{name}</span>
							</Link>
						</H4>
						<Paragraph size="tiny" className="address-paragraph">
							<span
								className="location-info"
								itemProp="address"
								itemScope
								itemType="http://schema.org/PostalAddress"
							>
								<Text rightSpacing>
									<span
										className="city"
										itemProp="addressLocality"
									>
										{' '}
										{city}
									</span>
									<span
										className="state"
										itemProp="addressRegion"
									>
										{' '}
										{state}
									</span>
									<span className="zip" itemProp="postalCode">
										{' '}
										{zip}
									</span>
								</Text>
								<Text>{distance}</Text>
							</span>
						</Paragraph>
					</div>
				</div>

				<div className="row facility-contact-links">
					{hasReviews && (
						<div className="star-and-promo">
							<FacilityReviewSummary
								rating={average}
								reviewCount={reviewAverage}
								facilityUrl={facilityUrl}
								noFollowFacilityLink={noFollowFacilityLink}
								color="gray"
								showRating
							/>
						</div>
					)}
				</div>

				<div className="facility-header-phone">
					<span>
						Need Help finding a storage unit? Give SpareFoot a call!
					</span>
					<ClickToShowNumberButton
						showAsLink
						className="phone-number-button"
						outline
						size="standard"
						fetchPhoneNumber={fetchSearchCityPhoneNumber}
						mobileLabel="Show phone number"
					/>
				</div>
			</div>
		</div>
	);
}

HiddenFacilityCardDetails.propTypes = {
	average: PropTypes.number,
	distance: PropTypes.string,
	facilityUrl: PropTypes.string,
	facilityImage: PropTypes.string,
	name: PropTypes.string,
	reviewAverage: PropTypes.number,
	streetAddress: PropTypes.string,
	city: PropTypes.string,
	state: PropTypes.string,
	zip: PropTypes.string,
	hasReviews: PropTypes.bool,
	noFollowFacilityLink: PropTypes.bool,
	fetchSearchCityPhoneNumber: PropTypes.func.isRequired,
	lazyLoad: PropTypes.bool
};
